/*
 * Copyright (c) 2019. ProVision Media Group Ltd. <https://provision.bg>
 * Venelin Iliev <http://veneliniliev.com>
 */

import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Logger } from '@app/core';
import { HotelsService } from '@app/hotels/hotels/hotels.service';
import { ApiQuery } from '@app/core/http/api-query';
import { Hotel } from '@app/hotels/hotels/hotel.model';
import { ActivitiesService } from '@app/shared/components/activities/activities.service';
import { LaravelModelsEnum } from '@app/shared/models/model.model';
import { HotelsFormService } from '@app/hotels/hotels/hotels-form.service';
import { HotelBookingsFormService } from '@app/hotels/hotel-bookings/hotel-bookings-form.service';
import { HotelBooking } from '@app/hotels/hotel-bookings/hotel-booking.model';
import LatLng = google.maps.LatLng;
import { MediaLibraryAcceptTypes } from '@app/shared/components/media-library/media-library-accept-types.enum';
import { PaginatedResponse } from '@app/shared/models/paginated-response.model';
import { DataStateChangeEvent, GridDataResult } from '@progress/kendo-angular-grid';
import { HotelBookingsService } from '@app/hotels/hotel-bookings/hotel-bookings.service';
import { environment } from '@env/environment';
import { KendoGridComponent } from '@app/shared/kendo/kendo-grid.component';
import { KendoGridService } from '@app/shared/kendo/kendo-grid.service';
import { SalesOffersService } from '@app/sales/sales/sales-offers.service';
import { CurrenciesAdditionsService } from '@app/shared/services/currencies-additions.service';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import TravelMode = google.maps.TravelMode;
import { DistanceService } from '@app/core/distance/distance.service';
import { ToastrService } from 'ngx-toastr';
import { Venue } from '@app/settings/venues/venue.model';
import { Title } from '@angular/platform-browser';
import { DataSet } from '@app/shared/kendo/data-set';
import { BookingPropertiesType } from '@app/settings/types/booking-properties-type.enum';
import { AccessService } from '@app/core/access/access.service';
import { UserRoleType } from '@app/settings/users/user-role-type.enum';
import { NgScrollbar } from 'ngx-scrollbar';
import { SalesFinalService } from '@app/sales/sales/sales-final.service';
import { GoogleMap, MapDirectionsRenderer } from '@angular/google-maps';

import * as moment from 'moment';

// @todo: може да се направи reset map функция

const log = new Logger('HotelsViewComponent');

@Component({
  selector: 'app-hotels-view',
  templateUrl: './hotels-view.component.html',
  styleUrls: ['./hotels-view.component.scss'],
})
export class HotelsViewComponent extends KendoGridComponent implements OnInit, OnDestroy {
  LaravelModels = LaravelModelsEnum;
  hotelId: number;
  hotel: Hotel;
  direction: any = false;
  markerOptions: {
    origin: {
      icon: 'assets/map_marker2.png';
    };
    destination: {
      icon: 'assets/map_marker.png';
    };
  };
  renderOptions = {
    suppressMarkers: true,
  };
  previous: any;
  MediaLibraryAcceptTypes = MediaLibraryAcceptTypes;
  env = environment;

  hotelBookingsRequestsResponse: PaginatedResponse;
  hotelBookingsData: GridDataResult;
  hotelBookingsState: DataStateChangeEvent;
  hotelBookingsApiQuery: ApiQuery = new ApiQuery();

  salesOffersItemsRequestsResponse: PaginatedResponse;
  salesOffersItemsData: GridDataResult;
  salesOffersItemsState: DataStateChangeEvent;
  salesOffersItemsApiQuery: ApiQuery = new ApiQuery();
  CurrenciesAdditionsService = CurrenciesAdditionsService;

  form: FormGroup;
  TravelMode = TravelMode;
  gMapsResults: any = null;
  selectedVenue: Venue = null;

  UserRoleType = UserRoleType;

  salesFinalsItemsRequestsResponse: PaginatedResponse;
  salesFinalsItemsData: GridDataResult;
  salesFinalsItemsState: DataStateChangeEvent;
  salesFinalsItemsApiQuery: ApiQuery = new ApiQuery();
  directionsDisplay!: google.maps.DirectionsRenderer;


  @ViewChild('perfectScroll', { static: true }) perfectScroll: NgScrollbar;
  @ViewChild('GoogleMap') map!: GoogleMap;

  constructor(
    private route: ActivatedRoute,
    // private router: Router,
    private hotelService: HotelsService,
    private hotelFormService: HotelsFormService,
    public activitiesService: ActivitiesService,
    private hotelBookingsFormService: HotelBookingsFormService,
    private hotelBookingService: HotelBookingsService,
    protected kendoGridService: KendoGridService,
    private salesOffersService: SalesOffersService,
    private formBuilder: FormBuilder,
    private distanceService: DistanceService,
    private toastr: ToastrService,
    private titleService: Title,
    public accessService: AccessService,
    public salesFinalsService: SalesFinalService
  ) {
    super(kendoGridService);
  }

  ngOnInit() {
    this.route.queryParamMap.subscribe((params) => {
      if (params.get('id')) {
        this.hotelBookingsState = new DataSet().addFilter('booking.property_id', BookingPropertiesType.ADV);

        this.hotelId = Number(params.get('id'));
        this.loadData(this.hotelId);
        this.hotelBookingsLoadDataTable(this.hotelBookingsState);
        this.salesOffersItemsLoadDataTable();
        this.salesFinalsItemsLoadDataTable();
        // this.openActivities();
        this.createForm();
      } else {
        log.error('Id is not found in route params!');
      }
    });
  }



  // View (template DOM) is fully rendered and can access ViewChild
  ngAfterViewInit() {
    this.tryInitRenderer();
  }

  tryInitRenderer(retries = 0): void {
    const maxRetries = 20;

    const googleReady =
      typeof google !== 'undefined' &&
      google.maps?.DirectionsRenderer;

    const mapReady = !!this.map?.googleMap;

    if (googleReady && mapReady) {
      this.directionsDisplay = new google.maps.DirectionsRenderer({
        suppressMarkers: true,
        polylineOptions: {
          strokeColor: '#4285F4',
          strokeOpacity: 0.8,
          strokeWeight: 5,
        },
      });
      this.directionsDisplay.setMap(this.map.googleMap);
      this.fitMapToHotelAndVenues();
    } else if (retries < maxRetries) {
      console.warn(`⏳ Waiting for Google Maps... (${retries})`);
      setTimeout(() => this.tryInitRenderer(retries + 1), 300);
    } else {
      console.error('❌ Failed to initialize DirectionsRenderer after retries');
    }
  }

  fitMapToHotelAndVenues(): void {
    if (!this.map?.googleMap || !this.hotel || !this.hotel.lat || !this.hotel.long || !this.hotel.venues?.length) { return; }

    const bounds = new google.maps.LatLngBounds();

    bounds.extend(new google.maps.LatLng(+this.hotel.lat, +this.hotel.long));

    for (const venue of this.hotel.venues) {
      if (venue.lat && venue.long) {
        bounds.extend(new google.maps.LatLng(+venue.lat, +venue.long));
      }
    }

    this.map.googleMap.fitBounds(bounds);
  }


  ngOnDestroy(): void {
    this.activitiesService.hide();
    if (this.directionsDisplay) {
      this.directionsDisplay.setMap(null);
      this.directionsDisplay = null as any;
    }
  }

  /**
   *  Парсва стрингосани числа
   * @param number Подава се стрингосано число или число
   */
  number(number: any): number {
    return Number(number);
  }

  // drawVenuesMarkers() {
  //   if (!this.hotel || !this.hotel.venues) {
  //     return console.warn('No venues to draw markers.');
  //   }
  //   this.hotel.venues.forEach((venue: VenueDistance) => {
  //
  //   });
  // }

  loadData(id: number): void {
    const apiQuery: ApiQuery = new ApiQuery().addIncludes(
      'bank_accounts',
      'contacts.type',
      'venues',
      'contact_persons.contacts.type',
      'contact_persons.event_group',
      'contact_persons.title',
      'type',
      'city.country',
      'gallery_count'
    );
    this.hotelService.show(id, apiQuery).subscribe((hotel: Hotel) => {
      log.debug(hotel);
      this.hotel = hotel;

      if (this.hotel.name) {
        this.titleService.setTitle(this.hotel.name);
      }
    });
  }

  openActivities() {
    this.activitiesService.show(LaravelModelsEnum.HOTEL, this.hotelId, false);
  }

  edit(): void {
    this.hotelFormService.open(this.hotel).then((response) => {
      log.debug('res', response);
      this.loadData(this.hotel.id);
    });
  }

  // add(): void {
  //   this.hotelFormService.open().then(
  //     response => {
  //       this.router.navigate([], {
  //         relativeTo: this.route,
  //         queryParams: { id: response.id },
  //         queryParamsHandling: 'merge'
  //       });
  //     },
  //     err => {
  //       log.info('modal closed');
  //     }
  //   );
  // }

  addBooking(): void {
    const newBooking = new HotelBooking().deserialize({
      hotel_id: this.hotelId,
      hotel: this.hotel,
      rooms: [
        {
          check_in: null,
          check_out: null,
          fake_net_price: null,
          fake_price: null,
          fake_web_price: null,
        },
      ],
    });

    this.hotelBookingsFormService.open(newBooking).then(
      (response) => {
        this.hotelBookingsLoadDataTable(this.hotelBookingsState);
      },
      (err) => {
        log.info('modal closed');
      }
    );
  }

  showVenueDirection(venue: any): void {
    this.selectedVenue = venue;

    if (!this.selectedVenue.lat || !this.selectedVenue.long) {
      this.direction = false;
    } else {
      this.direction = {
        origin: new google.maps.LatLng(this.hotel.lat, this.hotel.long),
        destination: new google.maps.LatLng(this.selectedVenue.lat, this.selectedVenue.long),
      };
      this.directionsDisplay.setMap(this.map.googleMap); // Reattach

      this.distanceService
        .drawRoute(
          { lat: this.hotel.lat, long: this.hotel.long },
          { lat: this.selectedVenue.lat, long: this.selectedVenue.long },
          this.map.googleMap,
          this.form.get('transport_type').value,
          this.directionsDisplay
        )
        .subscribe(
          (res: google.maps.DirectionsResult) => {
            const leg = res.routes[0]?.legs[0];
            if (leg) {
              this.gMapsResults = {
                distance: leg.distance.text,
                duration: leg.duration.text
              };
            }
          },
          (err) => {
            this.toastr.error(err);
            console.error(err);
          }
        );
    }
  }

  clickedMarker(infowindow: any) {
    if (this.previous) {
      this.previous.close();
    }
    this.previous = infowindow;
  }

  hotelBookingsLoadDataTable(state: DataStateChangeEvent = null) {
    log.debug('hotelBookingsLoadDataTable');
    this.hotelBookingsState = state;
    this.hotelBookingsApiQuery
      .addFilters({ 'hotel_booking_rooms.hotel_id': this.hotelId })
      .setDataSetFilters(state)
      .addIncludes(
        'booking',
        'booking.events',
        'booking.supplier',
        'booking.company',
        'booking.property',
        'booking.cancellation_terms',
        'type',
        'meal_plan',
        'nights',
        'nights.price_currency',
        'nights.net_price_currency'
      )
      .addFilter('full_available', true);

    this.hotelBookingService.indexRooms(this.hotelBookingsApiQuery).subscribe((response: PaginatedResponse) => {
      this.hotelBookingsRequestsResponse = response;
      this.hotelBookingsData = response.getGridData();
      this.perfectScroll.update();
    });
  }

  hotelBookingsDelete(hotelBooking: HotelBooking) {
    this.hotelBookingService.destroy(hotelBooking.id).subscribe(() => {
      this.hotelBookingsLoadDataTable(this.hotelBookingsState);
    });
  }

  hotelBookingsEdit(hotelBooking: HotelBooking) {
    this.hotelBookingsFormService.open(hotelBooking).then(
      (res) => {
        log.debug(res);
        this.hotelBookingsLoadDataTable(this.hotelBookingsState);
      },
      (err) => {
        log.debug(err);
      }
    );
  }

  salesOffersItemsLoadDataTable(state: DataStateChangeEvent = null) {
    this.salesOffersItemsState = state;
    this.salesOffersItemsApiQuery
      .addFilters({ hotel_id: this.hotelId })
      .setDataSetFilters(state)
      .addIncludes(
        'type',
        'meal_plan',
        'net_price_currency',
        'sale_offer.sale.event',
        'sale_offer.sale.client',
        'allotment.booking.property'
      );

    this.salesOffersService.itemsIndex(this.salesOffersItemsApiQuery).subscribe((response: PaginatedResponse) => {
      this.salesOffersItemsRequestsResponse = response;
      this.salesOffersItemsData = response.getGridData();
      this.perfectScroll.update();
    });
  }

  salesFinalsItemsLoadDataTable(state: DataStateChangeEvent = null) {
    this.salesFinalsItemsState = state;
    this.salesFinalsItemsApiQuery
      .addFilters({ 'hotel_booking_room.hotel_id': this.hotelId })
      .setDataSetFilters(state)
      .addIncludes(
        'saleFinal.sale.event.eventGroup',
        'saleFinal.sale.client',
        'saleFinal.sale.client.country',
        'hotel_booking_room.booking.company',
        'hotel_booking_room.booking.supplier',
        'hotel_booking_room.type',
        'hotel_booking_room.meal_plan',
        'saleOfferItem',
        'saleOfferItem.request.status',
        'hotel_booking_nights.net_price_currency'
      );

    this.salesFinalsService.itemsIndex(this.salesFinalsItemsApiQuery).subscribe((response: PaginatedResponse) => {
      response.data.forEach((item: any) => {
        const nights = this.getNightsCount(item.check_in, item.check_out);
        if (item.hotel_booking_nights) {
          let netPrice = 0;
          item.hotel_booking_nights.forEach((night: any, index: number) => {
            netPrice += CurrenciesAdditionsService.toEUR(
              Number(night.net_price),
              night.net_price_currency,
              night.exchange_rate
            );
          });
          item.hotel_booking_room['net_price'] = netPrice / nights / item.count;
        }
      });

      this.salesFinalsItemsRequestsResponse = response;
      this.salesFinalsItemsData = response.getGridData();
      this.perfectScroll.update();
    });
  }

  getNightsCount(date1: string | Date, date2: string | Date): number {
    if (!date1 || !date2) {
      return null;
    }
    const moment1 = moment(date1);
    const moment2 = moment(date2);
    return moment2.diff(moment1, 'days');
  }
  private createForm() {
      this.form = this.formBuilder.group({
        //TODO: issue with enum
        // Cannot read properties of undefined (reading 'DRIVING')
        transport_type: ['DRIVING', [Validators.required]],
      });
  }
}
